import type { DrawingDocument, DrawingEntity, DrawingPage, Point } from '../contract/types.js';
import { angleGap, candidateCost } from '../match/candidate.js';
import { optimalAssignment, INFEASIBLE } from '../match/hungarian.js';
import {
  ARC_ANGLE_TOLERANCE_DEG,
  drawingDiagonal,
  matchTolerance,
  TOLERANCE_DIAGONAL_FRACTION,
} from '../match/tolerance.js';
import { normaliseForComparison } from '../match/normalise.js';
import { toDrawingSpace } from '../transform/space.js';
import { distance } from '../match/geometry.js';

/**
 * Drawing version comparison.
 *
 * Six outcomes, and **ambiguous is one of them**. Where two readings are equally
 * supported, forcing a choice produces a confident wrong answer, which is worse
 * than an honest uncertain one. So a tie is reported as `ambiguous` with both
 * candidates' provenance retained, and never resolved to `changed`.
 *
 * Three properties are required of the result and hold by construction:
 *
 *   * **Symmetry.** Comparing A to B and B to A swaps added and removed and
 *     leaves everything else alone — the correspondences themselves, not merely
 *     the counts.
 *   * **Self-comparison.** A document compared to itself is entirely unchanged.
 *     This was false for any multi-sheet drawing until sheets were aligned
 *     before entities were matched; see ADR 0006.
 *   * **Identifier independence.** The result does not depend on internal
 *     entity identifiers. Matching never consults them.
 *
 * Two further rules are easy to lose and both were lost once:
 *
 *   * **Every reading of a difference is priced in one unit, a distance.** An
 *     entity may be untouched, moved, or edited where it stands, and those three
 *     compete honestly only if "which is more likely" compares like with like.
 *   * **Representation is not content.** A producer chooses which end of a line
 *     to write first and which corner of a rectangle to start from. Two exports
 *     of one drawing differ in exactly that, and a comparison that reads it as
 *     an edit reports changes nobody made.
 */

export type ChangeClass = 'added' | 'removed' | 'changed' | 'moved' | 'unchanged' | 'ambiguous';

export const CHANGE_CLASSES: readonly ChangeClass[] = [
  'added',
  'removed',
  'changed',
  'moved',
  'unchanged',
  'ambiguous',
];

/**
 * Thresholds, weights and tie-break rules live here and are versioned, so a
 * tuning change is a reviewable artefact rather than a silent behaviour shift.
 */
export interface MatcherProfile {
  version: string;
  /**
   * A translated entity counts as `moved` only within this fraction of the
   * drawing diagonal. Beyond it, the correspondence is not credible.
   */
  movedMaxDisplacementFraction: number;
  /**
   * Two candidates whose costs are this close are a tie, and a tie is
   * `ambiguous` rather than a coin toss.
   */
  tieRelativeTolerance: number;
  /**
   * How far an entity would have to travel before travelling becomes as
   * unlikely as being edited in place, as a fraction of the drawing diagonal.
   *
   * Correspondence costs are distances, so the two readings of a difference —
   * *it moved* and *it changed here* — have to be priced in the same units or
   * one of them always wins. They are not comparable on their own, so this is
   * the exchange rate, and it is a real judgement about drawings: an entity
   * that shifted a couple of millimetres was almost certainly nudged, and one
   * that would have had to cross a third of the sheet almost certainly did not.
   *
   * Without it, a line split in two here and a line joined from two there read
   * as four entities that swapped places, because any translation inside the
   * moved window is cheaper than any edit at all.
   */
  changeEquivalentDisplacementFraction: number;
  /**
   * How the sheets of the two versions are put in correspondence, before any
   * entity is. See {@link PageAlignmentRule}.
   */
  pageAlignment: PageAlignmentRule;
}

/**
 * How sheets are put in correspondence.
 *
 * `content` — align sheets by what is drawn on them, then match entities only
 * within an aligned pair. A sheet is the unit a reviewer reads, so correspondence
 * across sheets is not a claim geometry can support; but sheets are inserted,
 * deleted and reordered, so the alignment cannot simply be page number against
 * page number. An unaligned sheet is reported whole, as added or removed.
 *
 * `index` — page *i* against page *i*. Cheap, and wrong the moment a sheet is
 * inserted: every later sheet shifts and the whole drawing reads as rewritten.
 *
 * `none` — one pool across every sheet, which is what this module did first.
 * On a drawing whose sheets share a frame it dissolves into ambiguity, because
 * each entity on the shared frame has one indistinguishable counterpart per
 * sheet. Kept so the difference can be demonstrated rather than asserted.
 */
export type PageAlignmentRule = 'content' | 'index' | 'none';

export const DEFAULT_MATCHER_PROFILE: MatcherProfile = {
  version: '0.2.0',
  movedMaxDisplacementFraction: 0.25,
  tieRelativeTolerance: 0.05,
  changeEquivalentDisplacementFraction: 0.02,
  pageAlignment: 'content',
};

/**
 * Where one side of a change sits in its own version.
 *
 * Both sides are carried because a reviewer looking at a change needs to find
 * it in *both* drawings, and because entity identifiers are only unique within
 * one document — the same identifier string names a different entity in the
 * other version, so an identifier alone locates nothing.
 */
export interface DiffSide {
  entityId: string;
  pageIndex: number;
  /** [minX, minY, maxX, maxY] in the page space the entity was read from. */
  bbox: readonly [number, number, number, number];
  /** Index into the page's operator list, or -1 for text-layer items. */
  operatorIndex: number;
}

export interface DiffEntry {
  changeClass: ChangeClass;
  entityType: string;
  /** Absent for `added`. */
  base?: DiffSide;
  /** Absent for `removed`. */
  compare?: DiffSide;
  /**
   * Which aligned pair of sheets this change sits on: an index into the
   * result's `pageAlignment`, and **not a page number**. After a sheet is
   * inserted the two versions number the same sheet differently, so a single
   * page number cannot name where a change is. -1 when sheets were not aligned
   * at all.
   */
  sheetIndex: number;
  /** In the unit named by the result's `displacementUnit`. */
  displacement?: number;
  /** Retained for an ambiguous outcome, so a person can resolve it. */
  candidates?: DiffSide[];
  detail?: string;
}

export interface DiffSummary {
  byClass: Record<ChangeClass, number>;
  /**
   * Counts by page, given twice on purpose.
   *
   * The same integer names different sheets in the two versions once a sheet
   * has been inserted or removed, so a single by-page table would silently
   * report a change under a sheet number the reviewer cannot find it on. An
   * entity present in only one version appears only in that version's table.
   */
  byBasePage: Record<number, Partial<Record<ChangeClass, number>>>;
  byComparePage: Record<number, Partial<Record<ChangeClass, number>>>;
  ambiguous: number;
  /**
   * The share of **entities** not left undecided. Reported because without it a
   * matcher could meet any gate by abstaining.
   *
   * The denominator is every entity offered by both versions, not the number of
   * entries: an ambiguity emits one entry per implicated entity while a decided
   * correspondence emits one entry for two, so dividing by entries would mix
   * two different units and make the figure drift with the shape of the answer
   * rather than with how much of it was decided.
   */
  coverage: number;
}

/** Which sheet of one version answers to which sheet of the other. */
export interface PageAlignmentEntry {
  /** Null when the compare version gained this sheet. */
  basePageIndex: number | null;
  /** Null when the base version lost this sheet. */
  comparePageIndex: number | null;
  /**
   * 0 to 1. Two empty sheets are alike, so an empty pair scores 1.
   *
   * With no threshold in the way, this is the only thing separating a sheet
   * that was revised from a sheet that was replaced outright, so it is reported
   * rather than consumed.
   */
  similarity: number;
  /**
   * How far ahead of the next candidate this pairing was.
   *
   * Zero means the choice was a tie broken by sheet order, and a reviewer
   * should treat which sheet carries which changes as undecided. Negative means
   * the pairing was not this sheet's own best — the assignment gave it up for a
   * better total elsewhere.
   */
  margin: number;
}

export interface DiffResult {
  matcherProfileVersion: string;
  schemaVersion: string;
  comparisonSpace: 'drawing_mm' | 'normalised_page';
  /** True when scale was unknown on either side, so the comparison is relative. */
  partial: boolean;
  tolerance: number;
  /**
   * What `tolerance` and every `displacement` are measured in. At unknown scale
   * there is no millimetre to measure in, and a number shown as though there
   * were would be a fabrication.
   */
  displacementUnit: 'mm' | 'page_diagonal';
  basePageCount: number;
  comparePageCount: number;
  /**
   * How many entities each side offered.
   *
   * Without these, a drawing that was emptied and a drawing that could not be
   * read produce the same result object, and the difference between "everything
   * was deleted" and "nothing was extracted" is exactly the difference between
   * a finding and a failure.
   */
  baseEntityCount: number;
  compareEntityCount: number;
  pageAlignment: PageAlignmentEntry[];
  entries: DiffEntry[];
  summary: DiffSummary;
}

/** An entity moved into the comparison space, with where it came from. */
interface Comparable {
  entity: DrawingEntity;
  side: DiffSide;
}

/**
 * Geometry is compared in drawing millimetres. When physical scale is unknown on
 * either side there is no millimetre to compare in, so the comparison runs in
 * **normalised page space** and the result is marked partial rather than being
 * quietly presented as if it were dimensional.
 */
function toComparable(doc: DrawingDocument, normalise: boolean): Comparable[] {
  const out: Comparable[] = [];

  for (const page of doc.pages) {
    const diagonal = Math.hypot(page.widthPt, page.heightPt) || 1;
    for (const e of page.entities) {
      const entity = normalise
        ? toDrawingSpace(e, [1 / diagonal, 0, 0, 1 / diagonal, 0, 0])
        : toDrawingSpace(e, page.transform);
      out.push({
        entity,
        side: {
          entityId: e.id,
          pageIndex: page.index,
          bbox: e.source.bbox,
          operatorIndex: e.source.operatorIndex,
        },
      });
    }
  }
  return out;
}

export function compareDocuments(
  base: DrawingDocument,
  compare: DrawingDocument,
  profile: MatcherProfile = DEFAULT_MATCHER_PROFILE,
): DiffResult {
  const normalise = base.scaleState !== 'resolved' || compare.scaleState !== 'resolved';
  const a = toComparable(base, normalise);
  const b = toComparable(compare, normalise);

  const everything = [...a, ...b].map((c) => c.entity);
  const tolerance = normalise ? TOLERANCE_DIAGONAL_FRACTION : matchTolerance(drawingDiagonal(everything));
  const extent = normalise ? 1 : drawingDiagonal(everything) || 1;
  const maxDisplacement = extent * profile.movedMaxDisplacementFraction;
  const changeEquivalent = extent * profile.changeEquivalentDisplacementFraction;

  const pageAlignment = alignPages(base, compare, profile);

  const entries: DiffEntry[] = [];
  for (const [sheetIndex, left, right] of correspondenceGroups(a, b, pageAlignment, profile)) {
    entries.push(
      ...compareOneGroup(sheetIndex, left, right, tolerance, maxDisplacement, changeEquivalent, profile),
    );
  }

  // Deterministic order, independent of iteration order and of identifiers.
  entries.sort(
    (x, y) =>
      x.sheetIndex - y.sheetIndex ||
      x.entityType.localeCompare(y.entityType) ||
      x.changeClass.localeCompare(y.changeClass) ||
      (x.base?.entityId ?? '').localeCompare(y.base?.entityId ?? '') ||
      (x.compare?.entityId ?? '').localeCompare(y.compare?.entityId ?? ''),
  );

  return {
    matcherProfileVersion: profile.version,
    schemaVersion: base.contractVersion,
    comparisonSpace: normalise ? 'normalised_page' : 'drawing_mm',
    partial: normalise,
    tolerance,
    displacementUnit: normalise ? 'page_diagonal' : 'mm',
    basePageCount: base.pages.length,
    comparePageCount: compare.pages.length,
    baseEntityCount: a.length,
    compareEntityCount: b.length,
    pageAlignment,
    entries,
    summary: summarise(entries, a.length + b.length),
  };
}

/**
 * Puts the sheets of the two versions in correspondence.
 *
 * Similarity is a Jaccard score over coarse geometric signatures: enough to
 * recognise the same sheet through a revision, and far too coarse to decide
 * anything about an individual entity, which is the next step's work.
 *
 * Where two alignments score equally the one closest to the original sheet
 * order wins. That is a tie-break, and unlike a tie between two entities it is
 * not reported as ambiguous — refusing to align leaves both sheets whole, which
 * says less than choosing wrongly and is harder to check. So the choice is made
 * and then *published*: each pairing carries the similarity it scored and the
 * margin it won by, and a margin of zero says plainly that which sheet carries
 * which changes was decided by sheet order rather than by evidence.
 */
function alignPages(
  base: DrawingDocument,
  compare: DrawingDocument,
  profile: MatcherProfile,
): PageAlignmentEntry[] {
  if (profile.pageAlignment === 'none') return [];

  const basePages = base.pages.map((p) => p.index);
  const comparePages = compare.pages.map((p) => p.index);

  if (profile.pageAlignment === 'index') {
    // No similarity is measured on this rule, so none is claimed.
    const out: PageAlignmentEntry[] = [];
    for (const i of basePages) {
      const paired = comparePages.includes(i);
      out.push({
        basePageIndex: i,
        comparePageIndex: paired ? i : null,
        similarity: 0,
        margin: 0,
      });
    }
    for (const j of comparePages) {
      if (!basePages.includes(j)) {
        out.push({ basePageIndex: null, comparePageIndex: j, similarity: 0, margin: 0 });
      }
    }
    return out;
  }

  // Signatures are taken in page space and normalised by each sheet's own
  // diagonal, so a drawing re-issued on a different sheet size still aligns.
  // Taking them in the comparison space instead would key every signature off
  // the page height, and an A3 revision of an A4 sheet would score zero against
  // its own predecessor.
  const baseSignatures = base.pages.map((p) => sheetSignature(p));
  const compareSignatures = compare.pages.map((p) => sheetSignature(p));
  const similarity = baseSignatures.map((left) => compareSignatures.map((right) => jaccard(left, right)));

  // Every pairing stays feasible, however unalike. There is no threshold below
  // which two sheets stop being candidates, because the sheets left over are
  // already the ones the assignment could not use: on equal counts, refusing an
  // unalike pair and accepting it produce the same reading — everything on one
  // removed, everything on the other added — and on unequal counts the surplus
  // is unaligned either way. A threshold would only add a number to defend.
  //
  // The tie-break rides on the cost so the assignment resolves it, and is small
  // enough that it can never outweigh a real difference in similarity.
  const cost = similarity.map((row, i) => row.map((s, j) => 1 - s + 1e-6 * Math.abs(i - j)));

  const assignment =
    basePages.length > 0 && comparePages.length > 0 ? optimalAssignment(cost) : basePages.map(() => -1);

  const out: PageAlignmentEntry[] = [];
  const taken = new Set<number>();
  assignment.forEach((j, i) => {
    if (j < 0) {
      out.push({ basePageIndex: basePages[i]!, comparePageIndex: null, similarity: 0, margin: 0 });
      return;
    }
    taken.add(j);
    const row = similarity[i]!;
    const chosen = row[j]!;
    // The margin is taken along both axes and the smaller kept. Measured only
    // along this sheet's own row, it would report a comfortable win while the
    // sheet it was paired *to* had an equally good suitor of its own — which is
    // the same undecided question seen from the other side.
    const bestOtherInRow = row.filter((_, k) => k !== j).reduce((best, s) => Math.max(best, s), 0);
    const bestOtherInColumn = similarity
      .filter((_, k) => k !== i)
      .reduce((best, other) => Math.max(best, other[j] ?? 0), 0);
    const alternatives = Math.max(bestOtherInRow, bestOtherInColumn);
    const alone = row.length <= 1 && similarity.length <= 1;
    out.push({
      basePageIndex: basePages[i]!,
      comparePageIndex: comparePages[j]!,
      similarity: chosen,
      margin: alone ? chosen : chosen - alternatives,
    });
  });
  comparePages.forEach((p, j) => {
    if (!taken.has(j)) {
      out.push({ basePageIndex: null, comparePageIndex: p, similarity: 0, margin: 0 });
    }
  });

  const last = Number.MAX_SAFE_INTEGER;
  return out.sort(
    (x, y) =>
      (x.basePageIndex ?? last) - (y.basePageIndex ?? last) ||
      (x.comparePageIndex ?? last) - (y.comparePageIndex ?? last),
  );
}

/**
 * How coarse a sheet signature is, as a fraction of the sheet's own diagonal.
 * Far coarser than the matching tolerance: this decides which sheet is which,
 * not what happened on it, and it has to survive a revision unmoved.
 */
const SIGNATURE_QUANTUM = 0.004;

/** What is drawn on one sheet, described coarsely enough to recognise again. */
function sheetSignature(page: DrawingPage): Map<string, number> {
  const diagonal = Math.hypot(page.widthPt, page.heightPt) || 1;
  const counts = new Map<string, number>();
  for (const e of page.entities) {
    const key = signature(e, diagonal * SIGNATURE_QUANTUM);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/** A coarse description of what an entity is and roughly where it sits. */
function signature(e: DrawingEntity, quantum: number): string {
  const q = (n: number): number => Math.round(n / quantum);
  const [x0, y0, x1, y1] = boxOf(e);
  const where = `${e.type}:${q(x0)}:${q(y0)}:${q(x1)}:${q(y1)}`;
  return e.type === 'TEXT' || e.type === 'MTEXT' ? `${where}:${normaliseForComparison(e.text)}` : where;
}

function boxOf(e: DrawingEntity): [number, number, number, number] {
  switch (e.type) {
    case 'LINE':
      return [
        Math.min(e.start.x, e.end.x),
        Math.min(e.start.y, e.end.y),
        Math.max(e.start.x, e.end.x),
        Math.max(e.start.y, e.end.y),
      ];
    case 'LWPOLYLINE': {
      const xs = e.vertices.map((v) => v.x);
      const ys = e.vertices.map((v) => v.y);
      return xs.length === 0
        ? [0, 0, 0, 0]
        : [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
    }
    case 'CIRCLE':
    case 'ARC':
      return [
        e.centre.x - e.radius,
        e.centre.y - e.radius,
        e.centre.x + e.radius,
        e.centre.y + e.radius,
      ];
    case 'TEXT':
    case 'MTEXT':
      return [e.anchor.x, e.anchor.y, e.anchor.x, e.anchor.y];
  }
}

/** Multiset Jaccard. Two empty sheets are alike rather than undefined. */
function jaccard(left: Map<string, number>, right: Map<string, number>): number {
  let intersection = 0;
  let union = 0;
  for (const key of new Set([...left.keys(), ...right.keys()])) {
    const l = left.get(key) ?? 0;
    const r = right.get(key) ?? 0;
    intersection += Math.min(l, r);
    union += Math.max(l, r);
  }
  return union === 0 ? 1 : intersection / union;
}

/**
 * The sets within which a correspondence may be sought: one entity type on one
 * pair of aligned sheets. A group with an empty side is kept, because that is
 * exactly how a removed sheet or a new sheet must present itself.
 */
function correspondenceGroups(
  a: readonly Comparable[],
  b: readonly Comparable[],
  pageAlignment: readonly PageAlignmentEntry[],
  profile: MatcherProfile,
): [number, Comparable[], Comparable[]][] {
  const groups = new Map<string, [number, Comparable[], Comparable[]]>();
  const bucket = (sheet: number, type: string): [number, Comparable[], Comparable[]] => {
    const k = `${String(sheet + 1).padStart(6, '0')} ${type}`;
    const existing = groups.get(k);
    if (existing) return existing;
    const created: [number, Comparable[], Comparable[]] = [sheet, [], []];
    groups.set(k, created);
    return created;
  };

  if (profile.pageAlignment === 'none') {
    for (const c of a) bucket(-1, c.entity.type)[1].push(c);
    for (const c of b) bucket(-1, c.entity.type)[2].push(c);
    return [...groups.keys()].sort().map((k) => groups.get(k)!);
  }

  // Entities on sheets that answer to each other share a group. Entities on an
  // unaligned sheet get a group with nothing on the other side, which is how a
  // whole sheet comes to be reported as added or removed.
  const sheetOfBase = new Map<number, number>();
  const sheetOfCompare = new Map<number, number>();
  pageAlignment.forEach((pair, n) => {
    if (pair.basePageIndex !== null) sheetOfBase.set(pair.basePageIndex, n);
    if (pair.comparePageIndex !== null) sheetOfCompare.set(pair.comparePageIndex, n);
  });

  for (const c of a) bucket(sheetOfBase.get(c.side.pageIndex) ?? -1, c.entity.type)[1].push(c);
  for (const c of b) bucket(sheetOfCompare.get(c.side.pageIndex) ?? -1, c.entity.type)[2].push(c);

  return [...groups.keys()].sort().map((k) => groups.get(k)!);
}

function compareOneGroup(
  sheetIndex: number,
  left: readonly Comparable[],
  right: readonly Comparable[],
  tolerance: number,
  maxDisplacement: number,
  changeEquivalent: number,
  profile: MatcherProfile,
): DiffEntry[] {
  const out: DiffEntry[] = [];
  const usedLeft = new Set<number>();
  const usedRight = new Set<number>();

  // Cost is symmetric in the two documents, which is what makes the whole
  // comparison order-independent.
  const cell: (Correspondence | null)[][] = left.map((l) =>
    right.map((r) =>
      correspondenceFor(l.entity, r.entity, tolerance, maxDisplacement, changeEquivalent),
    ),
  );
  const cost: number[][] = cell.map((row) => row.map((c) => c?.cost ?? INFEASIBLE));

  const assignment =
    left.length > 0 && right.length > 0 ? optimalAssignment(cost) : left.map(() => -1);

  const assignedTo = new Map<number, number>();
  const assignedFrom = new Map<number, number>();
  assignment.forEach((j, i) => {
    if (j < 0 || !cell[i]?.[j]) return;
    assignedTo.set(i, j);
    assignedFrom.set(j, i);
  });

  /**
   * Whether two candidates are so alike that choosing between them would be a
   * coin toss.
   *
   * Only candidates offering the **same reading** are compared, and only on the
   * distance that distinguishes them. Comparing total costs instead put every
   * anchored candidate inside one window: an anchored cost spans a band exactly
   * `tolerance` wide, the relative window is at least that wide on any drawing
   * over 200 mm, and so any two edits about an anchor were unconditionally
   * declared tied.
   */
  const alike = (chosen: Correspondence, other: Correspondence | null): boolean => {
    if (other === null || other.reading !== chosen.reading) return false;
    const window = Math.max(
      chosen.distance * profile.tieRelativeTolerance,
      tolerance * TIE_ABSOLUTE_FRACTION,
    );
    return Math.abs(other.distance - chosen.distance) <= window;
  };

  /**
   * A tie is not a matching problem, it is an answer — but only a *live* rival
   * makes one.
   *
   * The assignment runs first, and a pairing is called ambiguous when something
   * else was just as good a candidate for either of its two entities. A rival
   * already claimed, decisively, by another pairing does not count: it is not
   * available, and counting it was what let one undecidable question elsewhere
   * on the sheet destroy a byte-identical match here — the identical entity was
   * masked before the assignment ever saw it, and coverage collapsed.
   */
  const undecided = new Set<number>();
  const rivals = (i: number, j: number): { rows: number[]; columns: number[] } => {
    const chosen = cell[i]![j]!;
    const claimedRight = (k: number): boolean => {
      const owner = assignedFrom.get(k);
      return owner !== undefined && !undecided.has(owner);
    };
    const claimedLeft = (k: number): boolean => assignedTo.has(k) && !undecided.has(k);
    return {
      rows: right
        .map((_, k) => k)
        .filter((k) => k !== j && alike(chosen, cell[i]![k]!) && !claimedRight(k)),
      columns: left
        .map((_, k) => k)
        .filter((k) => k !== i && alike(chosen, cell[k]![j]!) && !claimedLeft(k)),
    };
  };

  /**
   * Whether two assigned pairings could swap partners for the same total.
   *
   * Looking along one row and one column finds a rival for *one* entity. It does
   * not find the case where two pairings are individually distinguishable and
   * yet the two ways of pairing the four entities cost the same — a1 to b1 and
   * a2 to b2 at 1 and 3, against a1 to b2 and a2 to b1 at 2 and 2. The matcher
   * has no basis whatever for preferring one, and reporting either as decided
   * is exactly the coin toss this module refuses elsewhere.
   *
   * Checked pairwise over the assignment, which is quadratic in the entities of
   * one type on one sheet and finds every two-cycle. Longer cycles are not
   * enumerated: the general problem is finding all optimal assignments, which is
   * not tractable, and a three-way rotation of mutually interchangeable entities
   * is not a case any drawing has yet produced. The limitation is stated rather
   * than hidden.
   */
  const swappable = (i: number, j: number, k: number, l: number): boolean => {
    const straight = cell[i]![j]!.cost + cell[k]![l]!.cost;
    const crossedA = cell[i]![l]?.cost;
    const crossedB = cell[k]![j]?.cost;
    if (crossedA === undefined || crossedB === undefined) return false;
    if (!Number.isFinite(crossedA) || !Number.isFinite(crossedB)) return false;
    const crossed = crossedA + crossedB;
    const window = Math.max(straight * profile.tieRelativeTolerance, tolerance * TIE_ABSOLUTE_FRACTION);
    return Math.abs(crossed - straight) <= window;
  };

  // Freeing one pairing can free another, so this runs to a fixed point. It only
  // ever grows the undecided set, so it terminates.
  for (let settled = false; !settled; ) {
    settled = true;
    for (const [i, j] of assignedTo) {
      if (undecided.has(i)) continue;
      const { rows, columns } = rivals(i, j);
      if (rows.length > 0 || columns.length > 0) {
        undecided.add(i);
        settled = false;
        continue;
      }
      for (const [k, l] of assignedTo) {
        if (k === i || undecided.has(k) || !swappable(i, j, k, l)) continue;
        undecided.add(i);
        undecided.add(k);
        settled = false;
        break;
      }
    }
  }

  const ambiguousLeft = new Map<number, Set<number>>();
  const ambiguousRight = new Map<number, Set<number>>();
  const implicate = (map: Map<number, Set<number>>, index: number, partner: number): void => {
    const set = map.get(index) ?? new Set<number>();
    set.add(partner);
    map.set(index, set);
  };

  for (const i of [...undecided].sort((x, y) => x - y)) {
    const j = assignedTo.get(i)!;
    const { rows, columns } = rivals(i, j);
    implicate(ambiguousLeft, i, j);
    implicate(ambiguousRight, j, i);
    for (const k of rows) {
      implicate(ambiguousLeft, i, k);
      implicate(ambiguousRight, k, i);
    }
    for (const k of columns) {
      implicate(ambiguousLeft, k, j);
      implicate(ambiguousRight, j, k);
    }
  }

  for (const i of [...ambiguousLeft.keys()].sort((x, y) => x - y)) {
    usedLeft.add(i);
    const l = left[i]!;
    out.push({
      changeClass: 'ambiguous',
      entityType: l.entity.type,
      base: l.side,
      sheetIndex,
      candidates: sides([...ambiguousLeft.get(i)!], right),
      detail: 'two or more equally supported correspondences; not resolved',
    });
  }
  for (const j of [...ambiguousRight.keys()].sort((x, y) => x - y)) {
    usedRight.add(j);
    const r = right[j]!;
    out.push({
      changeClass: 'ambiguous',
      entityType: r.entity.type,
      compare: r.side,
      sheetIndex,
      candidates: sides([...ambiguousRight.get(j)!], left),
      detail: 'two or more equally supported correspondences; not resolved',
    });
  }

  assignment.forEach((j, i) => {
    if (j < 0 || usedLeft.has(i) || usedRight.has(j) || !cell[i]?.[j]) return;
    usedLeft.add(i);
    usedRight.add(j);
    const l = left[i]!;
    const r = right[j]!;
    const delta = translationBetween(l.entity, r.entity, tolerance);
    const displacement = delta === null ? undefined : Math.hypot(delta.x, delta.y);

    let changeClass: ChangeClass = 'changed';
    if (delta !== null && displacement !== undefined) {
      if (displacement <= tolerance && sameContent(l.entity, r.entity, tolerance)) {
        changeClass = 'unchanged';
      } else if (sameContent(l.entity, r.entity, tolerance) && displacement <= maxDisplacement) {
        changeClass = 'moved';
      }
    }

    out.push({
      changeClass,
      entityType: l.entity.type,
      base: l.side,
      compare: r.side,
      sheetIndex,
      ...(displacement === undefined ? {} : { displacement }),
      ...(changeClass === 'changed'
        ? {
            detail:
              delta === null
                ? 'geometry differs by more than a translation'
                : 'displaced beyond the moved threshold, or content differs',
          }
        : {}),
    });
  });

  left.forEach((l, i) => {
    if (usedLeft.has(i)) return;
    out.push({
      changeClass: 'removed',
      entityType: l.entity.type,
      base: l.side,
      sheetIndex,
    });
  });
  right.forEach((r, j) => {
    if (usedRight.has(j)) return;
    out.push({
      changeClass: 'added',
      entityType: r.entity.type,
      compare: r.side,
      sheetIndex,
    });
  });

  return out;
}

function sides(indices: readonly number[], from: readonly Comparable[]): DiffSide[] {
  return indices
    .map((i) => from[i]!.side)
    .sort((x, y) => x.pageIndex - y.pageIndex || x.entityId.localeCompare(y.entityId));
}

/**
 * How close two candidate distances have to be before the choice between them
 * is a coin toss, as a fraction of the matching tolerance. Below the tolerance
 * nothing is distinguishable anyway, so this is deliberately a small part of it.
 */
const TIE_ABSOLUTE_FRACTION = 0.05;

/** Which of the three accounts of a difference this correspondence rests on. */
type Reading = 'identical' | 'moved' | 'anchored';

interface Correspondence {
  reading: Reading;
  /** The distance that distinguishes this candidate from others of its reading. */
  distance: number;
  /** What the whole reading costs, on the one scale every reading is priced in. */
  cost: number;
}

/**
 * How plausible it is that these two entities are the same thing across
 * versions. Lower is better; null means no correspondence at all.
 *
 * **The cost is a distance**, and that is the point of it. Three readings of a
 * difference compete — it is untouched, it moved, it was edited where it stands
 * — and they can only be weighed against each other if they are priced in one
 * unit. So an untouched entity costs its geometric error, a moved one costs how
 * far it went, and an edited one costs the displacement its edit is worth
 * (`changeEquivalentDisplacementFraction`) plus how far its anchor drifted.
 *
 * The reading is returned alongside the cost, because comparing two candidates
 * for a *tie* is a different question from ranking them: the total prices the
 * reading, and only the distance within a reading distinguishes two candidates
 * offering the same one.
 *
 * Deliberately looser than the acceptance matcher: an entity that moved or
 * changed must still be *recognised* as the same entity, or every edit would
 * read as a removal plus an addition. But looser is not unconditional. The
 * weakest reading still demands evidence — an anchor the two versions share —
 * so that an entity deleted here and an unrelated one added there are never
 * presented as one entity that changed.
 */
function correspondenceFor(
  l: DrawingEntity,
  r: DrawingEntity,
  tolerance: number,
  maxDisplacement: number,
  changeEquivalent: number,
): Correspondence | null {
  if (l.type !== r.type) return null;

  // Identical within tolerance: the strongest correspondence there is.
  const exact = candidateCost(l, r, tolerance);
  if (exact !== null) return { reading: 'identical', distance: exact, cost: exact };

  const delta = translationBetween(l, r, tolerance);
  if (delta !== null && sameContent(l, r, tolerance)) {
    const d = Math.hypot(delta.x, delta.y);
    // A pure translation within the window: recognisably the same entity moved,
    // and priced at exactly how far it would have had to go.
    if (d <= maxDisplacement) return { reading: 'moved', distance: d, cost: d };
  }

  // Otherwise correspondence rests on a shared anchor: the part of an edited
  // entity that an edit leaves alone. A radius grows about its centre, a line
  // is extended from one end, a string is retyped at its insertion point. With
  // no such anchor there is nothing tying the two together, and the honest
  // report is a removal and an addition.
  const anchor = sharedAnchorDistance(l, r, tolerance);
  if (anchor === null) return null;
  return { reading: 'anchored', distance: anchor, cost: changeEquivalent + anchor };
}

/**
 * The distance between the closest anchor the two entities share, or null when
 * they share none within tolerance.
 */
function sharedAnchorDistance(l: DrawingEntity, r: DrawingEntity, tolerance: number): number | null {
  const best = (candidates: number[]): number | null => {
    const within = candidates.filter((d) => d <= tolerance);
    return within.length === 0 ? null : Math.min(...within);
  };

  switch (l.type) {
    case 'LINE': {
      const t = r as typeof l;
      return best([
        distance(l.start, t.start),
        distance(l.end, t.end),
        distance(l.start, t.end),
        distance(l.end, t.start),
        distance(midpoint(l.start, l.end), midpoint(t.start, t.end)),
      ]);
    }
    case 'LWPOLYLINE': {
      const t = r as typeof l;
      // A polyline is the same polyline edited when most of it stayed put, so
      // the anchor is a majority of its vertices rather than any single one.
      //
      // Counted in **both directions**, because "has a partner" is many-to-one:
      // two vertices closer together than the tolerance, or a duplicated vertex
      // of the kind path flattening emits routinely, both match one vertex of
      // the other polyline and inflate the count on one side only. Counting one
      // way made the whole comparison asymmetric — the same pair read `changed`
      // in one direction and removed-plus-added in the other, contradicting the
      // symmetry this module claims for itself.
      const near = (v: Point, among: readonly Point[]): boolean =>
        among.some((w) => distance(v, w) <= tolerance);
      const sharedFromBase = l.vertices.filter((v) => near(v, t.vertices)).length;
      const sharedFromCompare = t.vertices.filter((w) => near(w, l.vertices)).length;
      const needed = Math.max(1, Math.ceil(Math.max(l.vertices.length, t.vertices.length) / 2));
      if (Math.min(sharedFromBase, sharedFromCompare) < needed) return null;
      return 0;
    }
    case 'CIRCLE':
    case 'ARC': {
      const t = r as typeof l;
      return best([distance(l.centre, t.centre)]);
    }
    case 'TEXT':
    case 'MTEXT': {
      const t = r as typeof l;
      return best([distance(l.anchor, t.anchor)]);
    }
  }
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * The translation taking `l` onto `r`, or null if no single translation does.
 * Rotation and mirroring return null, so they classify as changed rather than
 * moved — a rotated part is not a moved part.
 *
 * Every comparison here is made **at the matching tolerance**, not at machine
 * precision. Both sides are recognised geometry: a circle is fitted to four
 * Bézier curves, and the same circle drawn a centimetre to the left fits to a
 * radius differing in the sixth decimal. Comparing those exactly answered *this
 * is a different circle*, and a circle that had merely moved was reported as
 * one removed and another added. Within the tolerance the board sets, two
 * shapes are the same shape; that is the rule the rest of the system uses and
 * it is the rule here.
 *
 * And **every equivalent way of writing the shape is tried**, not just the one
 * the producer happened to emit — see {@link equivalentPairings}.
 */
function translationBetween(l: DrawingEntity, r: DrawingEntity, tolerance: number): Point | null {
  if (l.type !== r.type) return null;

  let best: Point | null = null;
  for (const pairs of equivalentPairings(l, r, tolerance)) {
    if (pairs.length === 0) continue;
    const first = pairs[0]!;
    const delta = { x: first[1].x - first[0].x, y: first[1].y - first[0].y };
    const consistent = pairs.every(
      ([p, q]) =>
        Math.abs(q.x - p.x - delta.x) <= tolerance && Math.abs(q.y - p.y - delta.y) <= tolerance,
    );
    if (!consistent) continue;
    // The smallest translation that explains the difference, so the answer does
    // not depend on which equivalent pairing happened to be tried first.
    if (best === null || Math.hypot(delta.x, delta.y) < Math.hypot(best.x, best.y)) best = delta;
  }
  return best;
}

/** Beyond this many vertices, only forward and reverse traversal are tried. */
const MAX_CYCLIC_VERTICES = 64;

/**
 * Every way the two entities' points can be put in correspondence while still
 * describing the same shape.
 *
 * A PDF producer chooses which end of a line to write first, which corner of a
 * rectangle to start from, and which way round to traverse it. None of those is
 * a property of the drawing. Pairing point-for-point in the order the producer
 * emitted them read a line written backwards as *changed*, and a rectangle
 * started from the next corner as *changed* — on two exports of one drawing,
 * with the acceptance matcher simultaneously scoring them identical, because it
 * already tries every traversal (see `match/candidate.ts` and
 * docs/findings/07-polyline-traversal-is-unspecified.md). This is that same rule,
 * applied where it was missing.
 */
function equivalentPairings(
  l: DrawingEntity,
  r: DrawingEntity,
  tolerance: number,
): [Point, Point][][] {
  switch (l.type) {
    case 'LINE': {
      const t = r as typeof l;
      return [
        [
          [l.start, t.start],
          [l.end, t.end],
        ],
        [
          [l.start, t.end],
          [l.end, t.start],
        ],
      ];
    }
    case 'LWPOLYLINE': {
      const t = r as typeof l;
      if (t.vertices.length !== l.vertices.length) return [];
      const forward = t.vertices;
      const reversed = [...t.vertices].reverse();
      const orders = [forward, reversed];

      const out: [Point, Point][][] = [];
      const pair = (other: readonly Point[]): [Point, Point][] =>
        l.vertices.map((v, i) => [v, other[i]!] as [Point, Point]);

      for (const order of orders) {
        out.push(pair(order));
        // A closed polyline is the same shape from any starting vertex; an open
        // one is not, so only closed shapes get the cyclic shifts.
        if (!l.closed || order.length > MAX_CYCLIC_VERTICES) continue;
        for (let shift = 1; shift < order.length; shift++) {
          out.push(pair([...order.slice(shift), ...order.slice(0, shift)]));
        }
      }
      return out;
    }
    case 'CIRCLE': {
      const t = r as typeof l;
      if (Math.abs(t.radius - l.radius) > tolerance) return [];
      return [[[l.centre, t.centre]]];
    }
    case 'ARC': {
      const t = r as typeof l;
      if (Math.abs(t.radius - l.radius) > tolerance) return [];
      if (angleGap(t.startAngle, l.startAngle) > ARC_ANGLE_TOLERANCE_DEG) return [];
      if (angleGap(t.endAngle, l.endAngle) > ARC_ANGLE_TOLERANCE_DEG) return [];
      return [[[l.centre, t.centre]]];
    }
    case 'TEXT':
    case 'MTEXT': {
      const t = r as typeof l;
      return [[[l.anchor, t.anchor]]];
    }
  }
}

/**
 * Whether the two carry the same content, ignoring where they sit.
 *
 * "Content" is everything about an entity except its position, and the list is
 * longer than the points. A polyline that was open and is now closed traces a
 * different shape; text retyped at the same size still differs if its height or
 * its rotation changed. Comparing only the points and the string reported all of
 * those as **unchanged** — a difference the drawing genuinely contains, silently
 * dropped, while the acceptance matcher refused the same pairing outright
 * (`candidate.ts` returns null when `closed` differs: "a closed shape and an
 * open one with the same vertices are different shapes").
 */
function sameContent(l: DrawingEntity, r: DrawingEntity, tolerance: number): boolean {
  if (l.type !== r.type) return false;

  if (l.type === 'TEXT' || l.type === 'MTEXT') {
    const t = r as typeof l;
    if (normaliseForComparison(l.text) !== normaliseForComparison(t.text)) return false;
    if (Math.abs(l.height - t.height) > tolerance) return false;
    return angleGap(l.rotation, t.rotation) <= ARC_ANGLE_TOLERANCE_DEG;
  }

  if (l.type === 'LWPOLYLINE') {
    const t = r as typeof l;
    if (l.closed !== t.closed) return false;
    if (l.bulges.length !== t.bulges.length) return false;
    // A bulge is a shape, not a position: it is the arc a segment bows into.
    // Compared on its own scale rather than against a distance tolerance,
    // because a bulge is a ratio.
    if (l.bulges.some((b, i) => Math.abs(b - t.bulges[i]!) > BULGE_TOLERANCE)) return false;
  }

  return equivalentPairings(l, r, tolerance).length > 0;
}

/**
 * How close two bulges have to be to describe the same arc.
 *
 * A bulge is the tangent of a quarter of the segment's included angle, so it is
 * dimensionless and a distance tolerance cannot be applied to it. This is a
 * tenth of a degree of included angle at the shallow end, which is finer than
 * any drawing distinguishes.
 */
const BULGE_TOLERANCE = 1e-4;

function summarise(entries: readonly DiffEntry[], entityCount: number): DiffSummary {
  const byClass = Object.fromEntries(CHANGE_CLASSES.map((c) => [c, 0])) as Record<
    ChangeClass,
    number
  >;
  const byBasePage: Record<number, Partial<Record<ChangeClass, number>>> = {};
  const byComparePage: Record<number, Partial<Record<ChangeClass, number>>> = {};

  const tally = (
    table: Record<number, Partial<Record<ChangeClass, number>>>,
    page: number,
    changeClass: ChangeClass,
  ): void => {
    const bucket = (table[page] ??= {});
    bucket[changeClass] = (bucket[changeClass] ?? 0) + 1;
  };

  for (const e of entries) {
    byClass[e.changeClass] += 1;
    if (e.base) tally(byBasePage, e.base.pageIndex, e.changeClass);
    if (e.compare) tally(byComparePage, e.compare.pageIndex, e.changeClass);
  }

  // One ambiguous entry names one entity, so the count of ambiguous entries is
  // the count of undecided entities.
  const ambiguous = byClass.ambiguous;
  return {
    byClass,
    byBasePage,
    byComparePage,
    ambiguous,
    coverage: entityCount === 0 ? 1 : (entityCount - ambiguous) / entityCount,
  };
}
